/* src/modules/feedback/HRDiscussionBoard.js */
import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, addDoc, query, orderBy, onSnapshot, 
    doc, updateDoc, deleteDoc, serverTimestamp, arrayUnion, arrayRemove 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    ThumbUpIcon, ThumbDownIcon, TrashIcon, LockClosedIcon, 
    PlusIcon, PaperAirplaneIcon, SearchIcon, FilterIcon, 
    ClockIcon, UserIcon, ChevronLeftIcon, ChevronRightIcon 
} from '../../components/icons/Icons';

// --- HELPERS ---
const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    return new Date(timestamp.seconds * 1000).toLocaleDateString() + ' ' + 
           new Date(timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const UserAvatar = ({ url, name, className = "h-10 w-10" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return <img src={url} alt={name} className={`${className} rounded-full object-cover border border-gray-200 shrink-0`} onError={() => setImgError(true)} />;
    }
    return (
        <div className={`${className} rounded-full bg-blue-100 flex items-center justify-center text-[#213368] font-bold text-xs shrink-0 border border-blue-200`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-4 w-4" />}
        </div>
    );
};

export default function HRDiscussionBoard({ userProfile, appId, isHRAdmin }) {
    const [posts, setPosts] = useState([]);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newPost, setNewPost] = useState({ title: '', content: '' });
    const [expandedPostId, setExpandedPostId] = useState(null);
    
    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [timeFilter, setTimeFilter] = useState('All'); 

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/hr_discussions`), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [appId]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, timeFilter, itemsPerPage]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newPost.title.trim() || !newPost.content.trim()) return;
        
        await addDoc(collection(db, `artifacts/${appId}/hr_discussions`), {
            title: newPost.title,
            content: newPost.content,
            authorId: userProfile.id,
            authorName: userProfile.name,
            authorPhoto: userProfile.photoURL || null,
            authorDept: userProfile.department || 'General',
            createdAt: serverTimestamp(),
            status: 'Open',
            upvotes: [],
            downvotes: [],
            commentCount: 0
        });
        setNewPost({ title: '', content: '' });
        setIsCreateOpen(false);
    };

    const handleVote = async (post, type) => {
        if (post.status === 'Closed') {
            alert("Voting is disabled on closed threads.");
            return;
        }

        const postRef = doc(db, `artifacts/${appId}/hr_discussions`, post.id);
        const userId = userProfile.id;
        const batch = {};

        const currentUp = post.upvotes || [];
        const currentDown = post.downvotes || [];

        if (type === 'up') {
            batch.upvotes = currentUp.includes(userId) ? arrayRemove(userId) : arrayUnion(userId);
        } else {
            batch.downvotes = currentDown.includes(userId) ? arrayRemove(userId) : arrayUnion(userId);
        }
        await updateDoc(postRef, batch);
    };

    const handleDelete = async (postId) => {
        if (window.confirm("Delete this post permanently?")) {
            await deleteDoc(doc(db, `artifacts/${appId}/hr_discussions`, postId));
        }
    };

    const handleToggleStatus = async (post) => {
        const newStatus = post.status === 'Open' ? 'Closed' : 'Open';
        await updateDoc(doc(db, `artifacts/${appId}/hr_discussions`, post.id), { status: newStatus });
    };

    // Filter Logic
    const filteredPosts = useMemo(() => {
        return posts.filter(post => {
            const q = searchQuery.toLowerCase();
            const matchesSearch = !q || 
                post.title.toLowerCase().includes(q) || 
                post.content.toLowerCase().includes(q) || 
                post.authorName.toLowerCase().includes(q);

            let matchesTime = true;
            if (timeFilter !== 'All' && post.createdAt) {
                const date = post.createdAt.toDate();
                const now = new Date();
                const diffTime = Math.abs(now - date);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (timeFilter === 'Today') matchesTime = diffDays <= 1;
                else if (timeFilter === 'Week') matchesTime = diffDays <= 7;
                else if (timeFilter === 'Month') matchesTime = diffDays <= 30;
                else if (timeFilter === 'Year') matchesTime = diffDays <= 365; 
            }
            return matchesSearch && matchesTime;
        });
    }, [posts, searchQuery, timeFilter]);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredPosts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);

    return (
        <div className="p-6">
            {/* Header Controls */}
            <div className="flex flex-col xl:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex flex-col sm:flex-row gap-2 w-full xl:w-auto">
                    {/* Search */}
                    <div className="relative flex-1 sm:w-64">
                        <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Search topics, users..." 
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    
                    {/* Time Filter */}
                    <div className="relative">
                        <select 
                            className="w-full sm:w-auto appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20BEC6]"
                            value={timeFilter}
                            onChange={e => setTimeFilter(e.target.value)}
                        >
                            <option value="All">All Time</option>
                            <option value="Today">Today</option>
                            <option value="Week">This Week</option>
                            <option value="Month">This Month</option>
                            <option value="Year">This Year</option>
                        </select>
                        <FilterIcon className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>

                    {/* Pagination Size Selector */}
                    <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">Show:</span>
                        <select 
                            className="bg-white border border-gray-300 text-gray-700 py-2 px-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20BEC6]"
                            value={itemsPerPage}
                            onChange={e => setItemsPerPage(Number(e.target.value))}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                <button 
                    onClick={() => setIsCreateOpen(true)}
                    className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-[#20BEC6] text-white rounded-lg font-bold text-sm hover:opacity-90 transition shadow-sm whitespace-nowrap"
                >
                    <PlusIcon className="h-4 w-4 mr-1" /> New Post
                </button>
            </div>

            {/* Create Modal */}
            {isCreateOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                        <h3 className="text-xl font-bold text-[#213368] mb-4">Start a Discussion</h3>
                        <form onSubmit={handleCreate}>
                            <input className="w-full border border-gray-300 p-3 rounded-lg mb-3 focus:ring-2 focus:ring-[#20BEC6] outline-none" placeholder="Topic Title..." value={newPost.title} onChange={e => setNewPost({...newPost, title: e.target.value})} maxLength={100} required />
                            <textarea className="w-full border border-gray-300 p-3 rounded-lg mb-4 focus:ring-2 focus:ring-[#20BEC6] outline-none h-32" placeholder="What's on your mind?" value={newPost.content} onChange={e => setNewPost({...newPost, content: e.target.value})} required />
                            <div className="flex justify-end space-x-3">
                                <button type="button" onClick={() => setIsCreateOpen(false)} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg hover:opacity-90">Post</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Post List */}
            <div className="space-y-4 min-h-[300px]">
                {filteredPosts.length === 0 && <p className="text-center text-gray-400 py-10">No discussions found matching your filters.</p>}
                
                {currentItems.map(post => {
                    const isClosed = post.status === 'Closed';
                    return (
                        <div key={post.id} className={`border rounded-xl p-5 transition ${isClosed ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:shadow-sm'}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-4 w-full">
                                    
                                    {/* Vote Column */}
                                    <div className="flex flex-col items-center space-y-2 min-w-[50px] pt-1">
                                        <div className="flex flex-col items-center">
                                            <button onClick={() => handleVote(post, 'up')} disabled={isClosed} className={`p-1 rounded transition ${post.upvotes?.includes(userProfile.id) ? 'text-[#20BEC6] bg-teal-50' : 'text-gray-400 hover:bg-gray-100'} ${isClosed ? 'cursor-not-allowed opacity-50' : ''}`}>
                                                <ThumbUpIcon className="h-5 w-5" />
                                            </button>
                                            <span className="text-xs font-bold text-gray-600 mt-0.5">{post.upvotes?.length || 0}</span>
                                        </div>
                                        
                                        <div className="flex flex-col items-center">
                                            <button onClick={() => handleVote(post, 'down')} disabled={isClosed} className={`p-1 rounded transition ${post.downvotes?.includes(userProfile.id) ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:bg-gray-100'} ${isClosed ? 'cursor-not-allowed opacity-50' : ''}`}>
                                                <ThumbDownIcon className="h-5 w-5" />
                                            </button>
                                            <span className="text-xs font-bold text-gray-600 mt-0.5">{post.downvotes?.length || 0}</span>
                                        </div>
                                    </div>

                                    {/* Content (Wrapped in flex-1 min-w-0 to enable breaking) */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-lg font-bold text-[#213368] cursor-pointer hover:text-[#20BEC6] transition-colors truncate pr-2" onClick={() => setExpandedPostId(expandedPostId === post.id ? null : post.id)}>{post.title}</h4>
                                            {isClosed && <span className="shrink-0 text-[10px] bg-red-100 text-red-600 px-2 py-1 rounded-full font-bold uppercase border border-red-200 flex items-center"><LockClosedIcon className="h-3 w-3 mr-1"/> Closed</span>}
                                        </div>
                                        {/* ✅ FIX: break-words ensures text doesn't overflow */}
                                        <p className="text-sm text-gray-700 mt-2 leading-relaxed whitespace-pre-wrap break-words">{post.content}</p>
                                        
                                        <div className="flex items-center mt-4 pt-3 border-t border-gray-100">
                                            <UserAvatar url={post.authorPhoto} name={post.authorName} className="h-6 w-6 mr-2" />
                                            <div className="text-xs text-gray-500 flex items-center flex-wrap gap-2">
                                                <span className="font-bold text-[#213368]">{post.authorName}</span>
                                                <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{post.authorDept}</span>
                                                <span className="text-gray-300">•</span>
                                                <span className="flex items-center"><ClockIcon className="h-3 w-3 mr-1"/> {formatDate(post.createdAt)}</span>
                                                <span className="text-gray-300">•</span>
                                                <button className="text-blue-600 font-medium hover:underline" onClick={() => setExpandedPostId(expandedPostId === post.id ? null : post.id)}>{post.commentCount || 0} Comments</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isHRAdmin && (
                                    <div className="ml-4 flex flex-col space-y-2">
                                        <button onClick={() => handleDelete(post.id)} className="text-gray-300 hover:text-red-500 p-1" title="Delete Post"><TrashIcon className="h-4 w-4"/></button>
                                        <button onClick={() => handleToggleStatus(post)} className={`p-1 ${isClosed ? 'text-red-400 hover:text-green-500' : 'text-gray-300 hover:text-orange-500'}`} title={isClosed ? 'Reopen Thread' : 'Close Thread'}><LockClosedIcon className="h-4 w-4"/></button>
                                    </div>
                                )}
                            </div>
                            {expandedPostId === post.id && <CommentsSection appId={appId} postId={post.id} currentUser={userProfile} isHRAdmin={isHRAdmin} isClosed={isClosed} />}
                        </div>
                    );
                })}
            </div>

            {/* Pagination Footer */}
            {filteredPosts.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4">
                    <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredPosts.length)}</span> of <span className="font-medium">{filteredPosts.length}</span> results
                    </p>
                    <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Previous</span>
                            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <span className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Next</span>
                            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </nav>
                </div>
            )}
        </div>
    );
}

function CommentsSection({ appId, postId, currentUser, isHRAdmin, isClosed }) {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/hr_discussions/${postId}/comments`), orderBy('createdAt', 'asc'));
        const unsub = onSnapshot(q, (snap) => {
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [appId, postId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        await addDoc(collection(db, `artifacts/${appId}/hr_discussions/${postId}/comments`), {
            content: newComment,
            authorId: currentUser.id,
            authorName: currentUser.name,
            authorDept: currentUser.department || 'General',
            authorPhoto: currentUser.photoURL || null,
            createdAt: serverTimestamp()
        });
        
        await updateDoc(doc(db, `artifacts/${appId}/hr_discussions`, postId), {
            commentCount: (comments.length || 0) + 1
        });
        setNewComment('');
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Delete comment?")) return;
        await deleteDoc(doc(db, `artifacts/${appId}/hr_discussions/${postId}/comments`, commentId));
        await updateDoc(doc(db, `artifacts/${appId}/hr_discussions`, postId), {
            commentCount: Math.max(0, comments.length - 1)
        });
    };

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50/50 -mx-5 px-5 pb-2 rounded-b-xl">
            <div className="space-y-4 mb-4 max-h-80 overflow-y-auto pr-2">
                {comments.map(c => (
                    <div key={c.id} className="flex space-x-3 text-sm">
                        <UserAvatar url={c.authorPhoto} name={c.authorName} className="h-8 w-8 mt-1" />
                        {/* ✅ FIX: min-w-0 on container + break-words on text */}
                        <div className="flex-1 min-w-0 bg-white p-3 rounded-lg border border-gray-200 shadow-sm relative group">
                            <div className="flex items-baseline justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-[#213368] text-xs">{c.authorName}</span>
                                    <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 rounded">{c.authorDept}</span>
                                </div>
                                <span className="text-[10px] text-gray-400">{formatDate(c.createdAt)}</span>
                            </div>
                            <p className="text-gray-700 leading-snug whitespace-pre-wrap break-words">{c.content}</p>
                            {(isHRAdmin || c.authorId === currentUser.id) && (
                                <button onClick={() => handleDeleteComment(c.id)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"><TrashIcon className="h-3 w-3" /></button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {!isClosed ? (
                <form onSubmit={handleSubmit} className="flex gap-3">
                    <UserAvatar url={currentUser.photoURL} name={currentUser.name} className="h-8 w-8 hidden md:block" />
                    <div className="flex-1 relative">
                        <input className="w-full border border-gray-300 rounded-full pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#20BEC6] focus:border-transparent" placeholder="Write a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} />
                        <button type="submit" disabled={!newComment.trim()} className="absolute right-1 top-1 p-1.5 bg-[#213368] text-white rounded-full hover:opacity-90 disabled:opacity-50 disabled:bg-gray-300">
                            <PaperAirplaneIcon className="h-3 w-3" />
                        </button>
                    </div>
                </form>
            ) : (
                <div className="flex items-center justify-center p-2 bg-gray-100 rounded-lg text-gray-500 text-xs italic">
                    <LockClosedIcon className="h-3 w-3 mr-1"/> Thread is closed for comments.
                </div>
            )}
        </div>
    );
}